import { DestroyRef, Injectable, Injector, NgZone, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, exhaustMap, interval, map, of, repeat, switchMap, tap } from 'rxjs';
import { logger } from '../pbir/utils/logger';
import { catchAppError } from '../shared/utils';
import { AssetLockDto, AssetLockService } from './api.services';
import { UnlockNotificationDialogComponent } from '../shared/dialogs/unlock-notification-dialog/unlock-notification-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AppContext } from '../app/context';
import { AssetTypes } from '../shared/guards/asset-lock-guard';
import { environment } from '../../environments/environment';

const LOGGER = logger('AssetLockCheck');

const REFRESH_INTERVAL = environment.pullRates?.assetLocks ?? 5000;

@Injectable({
  providedIn: 'root'
})
export class AssetLocks {
  readonly destroyRef = inject(DestroyRef);
  readonly assetLockService = inject(AssetLockService);
  readonly injector = inject(Injector);
  readonly dialog = inject(MatDialog);
  readonly appContext = inject(AppContext);
  readonly zone = inject(NgZone);

  readonly visuals = new AssetLockSet(this, AssetTypes.Visual);
  readonly dashboards = new AssetLockSet(this, AssetTypes.Dashboard);
  readonly schedules = new AssetLockSet(this, AssetTypes.Schedule);
  readonly publishedReports = new AssetLockSet(this, AssetTypes.PublishedReport);

  refresh() {
    [this.visuals, this.dashboards, this.schedules, this.publishedReports].forEach(x => x.refresh());
  }
}

export class AssetLockSet {
  constructor(
    readonly owner: AssetLocks,
    readonly contentType: AssetTypes) {
    // random offset for jitter
    interval(REFRESH_INTERVAL + (Math.random() * 400)).pipe(
      exhaustMap(() => this._refresh())
    ).subscribe();
  }

  private _refresh() {
    return this.owner.assetLockService.getLocks(this.contentType).pipe(
      takeUntilDestroyed(this.owner.destroyRef),

      catchAppError(err => {
        LOGGER.error(`Failed to load '${this.contentType}' asset lock list`, err);
        return of([]);
      }, { showErrorSnack: false }),

      map(locks => new Map<number, AssetLockDto>(locks.map(x => [x.contentId, x]))),
      tap(locks => this.locks.set(locks)),
    );
  }

  refresh() {
    this._refresh().subscribe();
  }

  readonly locks  = signal(new Map<number, AssetLockDto>());
  readonly locks$ = toObservable(this.locks);

  isLocked(contentId: number) {
    let lock = this.locks().get(contentId);
    if (!this._isLocked(lock))
      return false;

    return true;
  }

  tryOpenLockedDialog(contentId: number): boolean {
    let lock = this.locks().get(contentId);
    if (!this._isLocked(lock))
      return false;

    this.owner.dialog.open(UnlockNotificationDialogComponent, {
      ...UnlockNotificationDialogComponent.defaults,
      data: lock,
    });

    return true;
  }

  private _isLocked(lock: AssetLockDto) {
    if (!lock)
      return false;

    if (lock.lockedByUserId == this.owner.appContext.user().id) 
      return false;

    return true;
  }
}
